import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Chip,
  Stack,
  Button,
  CircularProgress,
  Typography
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

/**
 * Tags input component with extraction functionality
 * Allows adding, editing, and removing tags
 */
const TagsInput = ({
  value = '',
  onChange,
  onExtract,
  disabled = false,
  extracting = false,
  label = 'Tags (comma separated)',
  helperText = 'Example: vacation, summer, beach',
  extractLabel = 'Extract Tags'
}) => {
  const [tags, setTags] = useState([]);
  
  // Parse tags from comma-separated string to array
  useEffect(() => {
    if (value) {
      const tagArray = value.split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      setTags(tagArray);
    } else {
      setTags([]);
    }
  }, [value]);
  
  // Delete a tag
  const handleDelete = (indexToDelete) => {
    const newTags = tags.filter((_, index) => index !== indexToDelete);
    const newValue = newTags.join(', ');
    onChange(newValue);
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    onChange(e.target.value);
  };
  
  // Extract tags using AI
  const handleExtract = () => {
    if (onExtract) {
      onExtract();
    }
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1">
          Tags
        </Typography>
        
        {onExtract && (
          <Button
            variant="outlined"
            size="small"
            startIcon={extracting ? <CircularProgress size={16} /> : <LocalOfferIcon />}
            onClick={handleExtract}
            disabled={disabled || extracting}
          >
            {extractLabel}
          </Button>
        )}
      </Box>
      
      <TextField
        fullWidth
        margin="normal"
        label={label}
        value={value}
        onChange={handleInputChange}
        disabled={disabled || extracting}
        helperText={helperText}
      />
      
      {tags.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
          {tags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={disabled ? undefined : () => handleDelete(index)}
              sx={{ height: 28 }} // Slightly taller for better touch targets
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default TagsInput;
